import { useState, useEffect, useContext } from "react";
import Context from "../../storage/context";
import axios from "axios";
import "./ReviewsEditor.css";

const ensureInputDateFormat = (dateVal) => {
    if (!dateVal || typeof dateVal !== 'string') return new Date().toISOString().split("T")[0];

    if (/^\d{4}-\d{2}-\d{2}/.test(dateVal)) {
        return dateVal.split("T")[0];
    }

    if (dateVal.includes('/')) {
        const parts = dateVal.split('/');
        if (parts.length === 3) {
            const [day, month, year] = parts;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }

    return new Date().toISOString().split("T")[0];
};

const ReviewsEditor = () => {
    const ctx = useContext(Context);
    const [reviews, setReviews] = useState([]);
    const [padingReviews, setPadingReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleteConfirmFile, setDeleteConfirmFile] = useState(null);
    const [showNewReview, setShowNewReview] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [newReview, setNewReview] = useState({
        name: "",
        city: "",
        text: "",
        rating: 5,
        date: new Date().toISOString().split("T")[0]
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    };

    const fetchReviews = async () => {
        try {
            const res = await axios.get(`${ctx.api_url}/reviews`, { headers });
            setReviews(res.data);
        } catch (err) {
            showMessage("שגיאה בטעינת ביקורות", "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchPadingReviews = async () => {
        try {
            const res = await axios.get(`${ctx.api_url}/pending-reviews`);
            setPadingReviews(res.data);
        } catch (err) {
            showMessage("שגיאה בטעינת ביקורות ממתנים לאישור", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    useEffect(() => {
        fetchPadingReviews();
    }, []);

    const handleDelete = async (id, loc) => {
        if (loc === "db") {
            try {
                await axios.delete(`${ctx.api_url}/reviews/${id}`, { headers });
                setReviews(reviews.filter(r => r.id !== id));
                setDeleteConfirm(null);
                showMessage("ביקורת נמחקה בהצלחה");
            } catch (err) {
                showMessage("שגיאה במחיקה", "error");
            }
        } else {
            try {
                await axios.delete(`${ctx.api_url}/delete-pending-review/${id}`, { headers });
                setDeleteConfirm(null);
                fetchPadingReviews();
                showMessage("ביקורת בהמתנה הוסרה מהרשימה");
            } catch (err2) {
                showMessage("שגיאה במחיקה", "error");
            }

        }
    };

    const handleAddReview = async () => {
        if (!newReview.name || !newReview.text) {
            showMessage("שם וטקסט הם שדות חובה", "error");
            return;
        }

        try {
            await axios.post(`${ctx.api_url}/reviews`, newReview, { headers });
            if (newReview?.id !== undefined) {
                try {
                    await axios.delete(`${ctx.api_url}/delete-pending-review/${newReview.id}`, { headers });
                } catch (err2) {
                    showMessage("שגיאה במחיקה", "error");
                }
            }
            setShowNewReview(false);
            setNewReview({
                name: "",
                city: "",
                text: "",
                rating: 5,
                date: new Date().toISOString().split("T")[0]
            });
            fetchReviews();
            fetchPadingReviews();
            showMessage("ביקורת נוספה בהצלחה");
        } catch (err) {
            showMessage("שגיאה בהוספת ביקורת", "error");
        }
    };

    const renderStars = (rating) => {
        return Array.from({ length: rating }).map((_, i) => (
            <span key={i}>⭐</span>
        ));
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const toApproveMessage = (review) => {
        setShowNewReview(true);
        setNewReview({
            ...review,
            date: ensureInputDateFormat(review.date)
        });
    };

    return (
        <section className="reviews-editor">
            <div className="reviews-header">
                <h2>ניהול ביקורות</h2>
                <button className="btn-add" onClick={() => setShowNewReview(!showNewReview)}>
                    {showNewReview ? "ביטול" : "+ ביקורת חדשה"}
                </button>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>{message.text}</div>
            )}

            {showNewReview && (
                <div className="new-review-form">
                    <h3>הוסף ביקורת חדשה</h3>
                    <div className="form-grid">
                        <input
                            type="text"
                            placeholder="שם *"
                            value={newReview.name}
                            onChange={e => setNewReview({ ...newReview, name: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="עיר"
                            value={newReview.city}
                            onChange={e => setNewReview({ ...newReview, city: e.target.value })}
                        />
                        <input
                            type="date"
                            value={newReview.date}
                            onChange={e => setNewReview({ ...newReview, date: e.target.value })}
                        />
                        <select
                            value={newReview.rating}
                            onChange={e => setNewReview({ ...newReview, rating: Number(e.target.value) })}
                            className="rating-select"
                        >
                            {[5, 4, 3, 2, 1].map(r => (
                                <option key={r} value={r}>{r} ⭐</option>
                            ))}
                        </select>
                        <textarea
                            placeholder="טקסט הביקורת *"
                            value={newReview.text}
                            onChange={e => setNewReview({ ...newReview, text: e.target.value })}
                            rows={3}
                            className="review-textarea"
                        />
                        <button className="btn-save" onClick={handleAddReview}>הוסף ביקורת</button>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="loading">טוען...</p>
            ) : reviews.length === 0 ? (
                <p className="loading">אין ביקורות עדיין</p>
            ) : (
                <table className="reviews-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>שם</th>
                            <th>עיר</th>
                            <th>ביקורת</th>
                            <th>דירוג</th>
                            <th>תאריך</th>
                            <th>פעולות</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reviews.map(review => (
                            <tr key={review.id}>
                                <td>{review.id}</td>
                                <td>{review.name}</td>
                                <td>{review.city || "—"}</td>
                                <td className="review-text-cell">{review.text}</td>
                                <td>{renderStars(review.rating)}</td>
                                <td>{formatDate(review.date)}</td>
                                <td>
                                    {deleteConfirm === review.id ? (
                                        <div className="confirm-row">
                                            <span>בטוח?</span>
                                            <button className="btn-delete" onClick={() => handleDelete(review.id, "db")}>כן</button>
                                            <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>לא</button>
                                        </div>
                                    ) : (
                                        <button className="btn-delete" onClick={() => setDeleteConfirm(review.id)}>מחק</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {padingReviews.map(review => (
                            <tr key={review.id}>
                                <td>{"New"}</td>
                                <td>{review.name}</td>
                                <td>{review.city || "—"}</td>
                                <td className="review-text-cell">{review.text}</td>
                                <td>{renderStars(review.rating)}</td>
                                <td>{review.date}</td>
                                <td>
                                    {deleteConfirmFile === review.id ? (
                                        <div className="confirm-row">
                                            <span>בטוח?</span>
                                            <button className="btn-delete" onClick={() => handleDelete(review.id, "file")}>כן</button>
                                            <button className="btn-cancel" onClick={() => setDeleteConfirmFile(null)}>לא</button>
                                        </div>
                                    ) : (
                                        <>
                                            <button className="btn-approve" onClick={() => toApproveMessage(review)}>לאישור הודעה</button>
                                            <button className="btn-delete" onClick={() => setDeleteConfirmFile(review.id)}>מחק</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
};

export default ReviewsEditor;
